import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { isMotionPaused } from '@/lib/motion'

interface PageTransitionProps {
    children: ReactNode
}

interface Mote {
    x: number
    y: number
    vx: number
    vy: number
    born: number
    life: number
    size: number
}

const pathOf = (url: string): string => url.split('#')[0].split('?')[0]

/**
 * Page changes: a thin blue line sweeps down, the old page dissolves into drifting points,
 * and the new page draws itself in.
 */
export const PageTransition: FC<PageTransitionProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const [leaving, setLeaving] = useState(false)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const lineRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        let raf = 0
        let motes: Array<Mote> = []

        const calm = (): boolean => isMotionPaused() || window.matchMedia('(prefers-reduced-motion: reduce)').matches

        const draw = (now: number): void => {
            const canvas = canvasRef.current
            const context = canvas?.getContext('2d')

            if (!canvas || !context) return

            const width = window.innerWidth
            const height = window.innerHeight

            context.clearRect(0, 0, width, height)
            motes = motes.filter(mote => now - mote.born < mote.life)

            for (const mote of motes) {
                const t = (now - mote.born) / mote.life
                const age = (now - mote.born) / 1000
                const alpha = t < 0.25 ? t / 0.25 : 1 - (t - 0.25) / 0.75

                context.fillStyle = `rgba(216, 210, 196, ${(alpha * 0.7).toFixed(3)})`
                context.fillRect(mote.x + mote.vx * age, mote.y + mote.vy * age, mote.size, mote.size)
            }

            raf = motes.length > 0 ? requestAnimationFrame(draw) : 0
        }

        const dissolve = (): void => {
            const canvas = canvasRef.current
            const context = canvas?.getContext('2d')

            if (!canvas || !context) return

            const ratio = Math.min(window.devicePixelRatio || 1, 2)
            const width = window.innerWidth
            const height = window.innerHeight

            canvas.width = Math.round(width * ratio)
            canvas.height = Math.round(height * ratio)
            context.setTransform(ratio, 0, 0, ratio, 0, 0)

            const now = performance.now()

            for (let i = 0; i < 900; i++) {
                motes.push({
                    x: Math.random() * width,
                    y: Math.random() * height,
                    vx: (Math.random() - 0.5) * 24,
                    vy: -12 - Math.random() * 46,
                    born: now + Math.random() * 360,
                    life: 700 + Math.random() * 700,
                    size: Math.random() < 0.2 ? 2 : 1.4
                })
            }

            if (!raf) raf = requestAnimationFrame(draw)
        }

        const sweep = (): void => {
            const line = lineRef.current

            if (!line || typeof line.animate !== 'function') return

            line.animate(
                [
                    { transform: 'translate3d(0, 0, 0)', opacity: 1 },
                    { transform: `translate3d(0, ${window.innerHeight}px, 0)`, opacity: 1, offset: 0.85 },
                    { transform: `translate3d(0, ${window.innerHeight}px, 0)`, opacity: 0 }
                ],
                { duration: 820, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' }
            )
        }

        const onStart = (url: string, options: { shallow?: boolean }): void => {
            if (options?.shallow || pathOf(url) === window.location.pathname) return

            setLeaving(true)

            if (calm()) return

            sweep()
            dissolve()
        }

        const onDone = (): void => {
            setLeaving(false)
        }

        router.events.on('routeChangeStart', onStart)
        router.events.on('routeChangeComplete', onDone)
        router.events.on('routeChangeError', onDone)

        return () => {
            router.events.off('routeChangeStart', onStart)
            router.events.off('routeChangeComplete', onDone)
            router.events.off('routeChangeError', onDone)
            cancelAnimationFrame(raf)
        }
    }, [router.events])

    return (
        <>
            <div className={cn('page-shell', leaving && 'is-leaving')}>
                {children}
            </div>

            <div className='pointer-events-none fixed inset-0 z-[80]' aria-hidden='true'>
                <canvas ref={canvasRef} className='absolute inset-0 h-full w-full' />
                <span ref={lineRef} className='absolute inset-x-0 top-0 h-px bg-signal opacity-0' />
            </div>
        </>
    )
}
